import React, { createContext, useContext, useState, useEffect } from 'react';
import { useOnboarding } from '@/hooks/use-onboarding';

type OnboardingContextType = ReturnType<typeof useOnboarding>;

const defaultContextValue: OnboardingContextType = {
  isLoading: true,
  userData: null,
  onboardingComplete: false,
  resetOnboarding: () => console.warn('resetOnboarding called before provider ready'),
  loadUserData: () => console.warn('loadUserData called before provider ready'),
  saveUserData: () => console.warn('saveUserData called before provider ready')
};

const OnboardingContext = createContext<OnboardingContextType>(defaultContextValue);

// Safe OnboardingProvider component that handles initialization errors
export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isReady, setIsReady] = useState(false);
  const [contextValue, setContextValue] = useState<OnboardingContextType>(defaultContextValue);
  
  // Use the hook safely
  const onboardingData = useOnboarding();
  
  useEffect(() => {
    // Update context value when hook data changes
    setContextValue(onboardingData);
    setIsReady(true);
  }, [onboardingData]);
  
  return (
    <OnboardingContext.Provider value={isReady ? contextValue : defaultContextValue}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboardingContext = (): OnboardingContextType => {
  // Safe context access - always returns a value (never undefined/null)
  return useContext(OnboardingContext);
};
